import { DialogComponent } from '@theme/dialog'
import { CartAddEvent, ThemeEvents } from '@theme/events'
import { Component } from '@theme/component'

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback()
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd)
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog()
    }
  }

  open() {
    this.showDialog()

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot
      const cta = installmentsContent?.querySelector(
        '#shopify-installments-cta'
      )
      cta?.addEventListener('click', this.closeDialog, { once: true })
    })
  }

  close() {
    this.closeDialog()
  }
}

/**
 * A custom element that displays cart count in text format.
 */
class CartCountText extends Component {
  /** @type {number} */
  get currentCartCount() {
    const element = Array.isArray(this.refs.cartBubbleCount)
      ? this.refs.cartBubbleCount[0]
      : this.refs.cartBubbleCount
    return parseInt(element?.textContent ?? '0', 10)
  }

  set currentCartCount(value) {
    const element = Array.isArray(this.refs.cartBubbleCount)
      ? this.refs.cartBubbleCount[0]
      : this.refs.cartBubbleCount
    if (element) {
      element.textContent = String(value)
    }
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
  }

  /**
   * Handles cart update events.
   * @param {CustomEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const itemCount = event.detail?.data?.itemCount ?? 0
    const comingFromProductForm =
      event.detail?.data?.source === 'product-form-component'

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent)
}

if (!customElements.get('cart-count-text')) {
  customElements.define('cart-count-text', CartCountText)
}
